import React, { useEffect, useCallback } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import { navigationService } from '__common/services/navigationService';

import ApplicationRevision from '_containers/ApplicationRevision';
import Bom from '_containers/Bom';
import ChangePassword from '_containers/ChangePassword';
import CombinedProjects from '_containers/CombinedProjects';
import ConfirmClearArraysModal from 'projectDesign/components/confirmClearArraysModal/ConfirmClearArraysModal';
import DesignAssumptions from '_containers/DesignAssumptions';
import DisableAccount from '_containers/DisableAccount';
import Engineering from '_containers/Engineering';
import ForgotPassword from '_containers/ForgotPassword';
import HelioscopeDesignConfig from '_containers/HelioscopeDesignConfig';
import HelioscopeDesigns from '_containers/HelioscopeDesigns';
import HelioscopeImport from '_containers/HelioscopeImport';
import Home from '_containers/Home';
import Loader from '__common/components/LoaderComponent';
import LoginPage from '_containers/LoginPage';
import NotFound from '_containers/NotFound';
import ProjectDesign from '_containers/ProjectDesign';
import Registration from '_containers/Registration';
import ReportProblemForm from 'app/components/ReportProblem/ReportProblemForm';
import FrequentlyAskedQuestions from 'app/components/FaqComponent/Faq';
import WhatsNew from 'app/components/WhatsNew/WhatsNew';
import ResetPassword from '_containers/ResetPassword';
import ScreenshotContainer from './ScreenshotContainer';
import TermsofService from '_containers/TermsOfService';
import UserPreferences from '_containers/UserPreferences';
import YourProjects from '_containers/YourProjects';
import { detect } from 'detect-browser';
import { SET_APP_ERROR, LOCATION_CHANGE } from 'actionsConstants';
import { SEARCH_PROJECT } from 'actions';
import { tryToLogIn } from 'user/user';
import ErrorPageModal from '__common/components/ErrorPageModal';
import { GET_APP_STATUS } from 'app/appActions';
import UBuilderLiteContainer from './UBuilderLiteContainer';
import LiteDashboard from 'v3/dashboard/LiteDashboard';
import CompleteBomPage from 'v3/bomPage/CompleteBomPage';
import ExposureRecalculationModal from 'projectDesign/components/ExposureRecalculationModal';
import CalculateZoneClassificationModal from 'projectDesign/components/CalculateZoneClassification';
import InfoModal from 'app/components/InfoModal/InfoModal';
import { googleAnalyticsActions } from '__common/utils/googleAnalytics/google_analytics_init';
import AuroraImport from './AuroraImport';
import AuroraDesignConfig from './AuroraDesignConfig';
import OpenSolarImport from './OpenSolarImport';
import OpenSolarDesignConfig from './OpenSolarDesignConfig';

import ReactGA from 'react-ga';
import { captureException } from '__common/utils/sentry';
import { parseDataForBomSave } from 'bom/bomHelpers';
import products from '__common/utils/products';

const googleAnalyticsTrackingId = require('local_config').googleAnalyticsTrackingId;
googleAnalyticsActions.initiateGA(googleAnalyticsTrackingId);

// Navigation effects handler for routing side effects
interface NavigationHandlerProps {
  dispatch: (action: any) => void;
  projectConfiguration: {
    projectId?: string;
  } | null;
}

const NavigationHandler: React.FC<NavigationHandlerProps> = ({ dispatch, projectConfiguration }) => {
  const location = useLocation();

  const handleLocationChange = useCallback(() => {
    // Track page view in Google Analytics
    ReactGA.set({ page: location.pathname });
    ReactGA.pageview(location.pathname);

    const projectIdFromState = projectConfiguration?.projectId;
    const { pathname } = location;
    const pathSegments = pathname.split('/');
    const projectIdRegex = /^[a-zA-Z0-9]{8}$/;

    // Handle project ID in URL for design page
    if (pathname.includes('/project/design') && pathSegments.length === 5 && projectIdRegex.test(projectIdFromState || '')) {
      const projectIdInPath = pathSegments[pathSegments.length - 1];
      if (projectIdInPath !== projectIdFromState) {
        dispatch(SEARCH_PROJECT(projectIdInPath));
      }
    }

    // Dispatch location change action to update router state
    dispatch({
      type: LOCATION_CHANGE,
      payload: {
        location,
        action: 'POP',
      },
    });
  }, [location, projectConfiguration, dispatch]);

  useEffect(() => {
    handleLocationChange();
  }, [handleLocationChange]);

  return null;
};

/* ---------------- ROUTE GUARDS ---------------- */

const ProtectedRoute = ({ children, isAuthenticated, accessibleUrl = true }: {
  children: React.ReactNode;
  isAuthenticated: boolean;
  accessibleUrl?: boolean;
}) => {
  // Prevent navigation during zone classification to avoid state corruption
  const appState = useSelector((state: any) => state);
  const isZoneCalculating = appState.settings?.calculateZoneClassication || false;
  
  // Allow access if authenticated OR if zone classification is running (preserves existing session)
  if (!isAuthenticated && !isZoneCalculating) return <Navigate to="/login" replace />;
  if (!accessibleUrl) return <Navigate to="/" replace />;
  return <>{children}</>;
};

const PublicRoute = ({ children, isAuthenticated }: {
  children: React.ReactNode;
  isAuthenticated: boolean;
}) => {
  if (isAuthenticated) return <Navigate to="/" replace />;
  return <>{children}</>;
};

/* ---------------- PARAM WRAPPERS ---------------- */

const withParams = (Component: any) => () => {
  const params = useParams();
  const location = useLocation();
  return <Component match={{ params }} location={location} />;
};

const HelioscopeImportWrapper = withParams(HelioscopeImport);
const HelioscopeDesignsWrapper = withParams(HelioscopeDesigns);
const HelioscopeDesignConfigWrapper = withParams(HelioscopeDesignConfig);
const ProjectDesignWrapper = withParams(ProjectDesign);
const AuroraImportWrapper = withParams(AuroraImport);
const AuroraDesignConfigWrapper = withParams(AuroraDesignConfig);
const OpenSolarImportWrapper = withParams(OpenSolarImport);
const OpenSolarDesignConfigWrapper = withParams(OpenSolarDesignConfig);
const CombinedProjectsWrapper = withParams(CombinedProjects);
const ForgotPasswordWrapper = withParams(ForgotPassword);
const ScreenshotContainerWrapper = withParams(ScreenshotContainer);
const BomWrapper = withParams(Bom);
const EngineeringWrapper = withParams(Engineering);
const UBuilderLiteContainerWrapper = withParams(UBuilderLiteContainer);
const CompleteBomPageWrapper = withParams(CompleteBomPage);

// Same Aurora launch URL as design.unirac.com: /project/design/aurora?aurora_*=...
// There, componentWillMount rewrites it to the path route before any login check.
// On design2 that rewrite is in useEffect, which runs after tryToLogIn() renders
// routes. /project/design/:productId matches first and sends the iframe to SSO.
const AuroraQueryEntry = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const aurora_tenant_id = params.get('aurora_tenant_id');
  const aurora_project_id = params.get('aurora_project_id');
  const aurora_design_id = params.get('aurora_design_id');
  const aurora_user_id = params.get('aurora_user_id');
  const aurora_session_token = params.get('aurora_session_token');

  if (!aurora_tenant_id || !aurora_project_id || !aurora_design_id || !aurora_user_id || !aurora_session_token) {
    return <NotFound />;
  }

  return (
    <AuroraImport
      match={{
        params: {
          aurora_tenant_id,
          aurora_project_id,
          aurora_design_id,
          aurora_user_id,
          aurora_session_token,
        },
      }}
    />
  );
};

/* ---------------- APP ---------------- */

type PropsFromRedux = ConnectedProps<typeof connector>;

// Error boundary component
class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error Boundary caught an error:', error, errorInfo);
    // You can dispatch to Redux store here if needed
    captureException(error);
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPageModal />;
    }

    return this.props.children;
  }
}

const App: React.FC<PropsFromRedux> = ({ dispatch, user, app, projectConfiguration }) => {

  /* Auto login + app status */
  const navigate = useNavigate();

  // Initialize navigation service
  useEffect(() => {
    navigationService.setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    tryToLogIn();
    dispatch(GET_APP_STATUS());
  }, [dispatch]);

  /* IE detection */
  useEffect(() => {
    const browser = detect();
    if (browser?.name === 'ie') dispatch(SET_APP_ERROR('', '', true));
  }, [dispatch]);

  /* 🔥 OpenSolar exit → send BOM back */
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (event.data?.type === 'OPENSOLAR_EXIT_DIALOG') {
        const { state } = await import('__common/store');
        const appState = state();

        const {
          projectConfiguration: { productId },
          bom: { partsList, extraParameters },
          railsSelector: { railsList, selectOnlySMHDRails }
        } = appState;

        const bomData = parseDataForBomSave(
          partsList,
          railsList,
          extraParameters,
          products[productId],
          selectOnlySMHDRails
        );

        window.parent.postMessage(
          { type: 'UBUILDER_BOM_READY', payload: bomData || null },
          '*'
        );
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const isAuroraAccessible = true;

  if (user.isRunning) return <Loader />;

  const isProjectRequestRunning = app.searchProject || app.copyProject;

  return (
    <AppErrorBoundary>
        {isProjectRequestRunning && (
          <div className="app-route-loader-overlay">
            <Loader fitParent />
          </div>
        )}
        <NavigationHandler dispatch={dispatch} projectConfiguration={projectConfiguration} />
        <div className="app">
          <CalculateZoneClassificationModal />
          <ConfirmClearArraysModal />
          <ReportProblemForm />
          <FrequentlyAskedQuestions />
          <WhatsNew />
          <InfoModal />
          <ErrorPageModal />
          <ExposureRecalculationModal />

          <main className="app__main">
            <Routes>
              <Route path="/" element={<Home />} />

              <Route path="/login" element={
                <PublicRoute isAuthenticated={user.isAuthenticated}>
                  <LoginPage dispatch={dispatch} user={user} />
                </PublicRoute>
              } />

              <Route path="/registration" element={
                <PublicRoute isAuthenticated={user.isAuthenticated}>
                  <Registration dispatch={dispatch} user={user} />
                </PublicRoute>
              } />

              <Route path="/hs/list" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><HelioscopeImportWrapper /></ProtectedRoute>} />
              <Route path="/hs/project/:projectId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><HelioscopeDesignsWrapper /></ProtectedRoute>} />
              <Route path="/hs/design/config/:projectId/:designId/:fieldSegmentId/:productId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><HelioscopeDesignConfigWrapper /></ProtectedRoute>} />
              <Route path="/hs/design/config/:projectId/:designId/:productId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><HelioscopeDesignConfigWrapper /></ProtectedRoute>} />

              <Route path="/lite/dashboard" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><LiteDashboard /></ProtectedRoute>} />
              <Route path="/project/lite/design/:productId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><UBuilderLiteContainerWrapper /></ProtectedRoute>} />
              <Route path="/project/lite/design/:productId/:projectId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><UBuilderLiteContainerWrapper /></ProtectedRoute>} />
              <Route path="/project/lite/bom/:productId/:projectId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><CompleteBomPageWrapper /></ProtectedRoute>} />

              <Route path="/project/design/aurora" element={<AuroraQueryEntry />} />
              <Route path="/project/design/:productId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><ProjectDesignWrapper /></ProtectedRoute>} />
              <Route path="/project/design/:productId/:projectId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><ProjectDesignWrapper /></ProtectedRoute>} />

              <Route path="/project/design/aurora/:aurora_tenant_id/:aurora_project_id/:aurora_design_id/:aurora_user_id/:aurora_session_token"
                element={<ProtectedRoute isAuthenticated={true} accessibleUrl={isAuroraAccessible}><AuroraImportWrapper /></ProtectedRoute>} />
              <Route path="/opensolar" element={<PublicRoute isAuthenticated={user.isAuthenticated}><OpenSolarImportWrapper /></PublicRoute>} />
              <Route path="/opensolar_design" element={<ProtectedRoute isAuthenticated={true}><OpenSolarDesignConfigWrapper /></ProtectedRoute>} />

              <Route path="/project/bom/:productId/:projectId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><BomWrapper /></ProtectedRoute>} />
              <Route path="/project/engineering/:productId/:projectId" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><EngineeringWrapper /></ProtectedRoute>} />
              <Route path="/yourprojects" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><YourProjects /></ProtectedRoute>} />
              <Route path="/userpreferences" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><UserPreferences /></ProtectedRoute>} />
              <Route path="/changepassword" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><ChangePassword /></ProtectedRoute>} />
              <Route path="/ChangePassword" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><ChangePassword /></ProtectedRoute>} />
              <Route path="/disableaccount" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><DisableAccount /></ProtectedRoute>} />
              <Route path="/DisableAccount" element={<ProtectedRoute isAuthenticated={user.isAuthenticated}><DisableAccount /></ProtectedRoute>} />
              <Route path="/applicationrevision" element={<ApplicationRevision />} />
              <Route path="/termsofservice" element={<TermsofService />} />
              <Route path="/designassumptions" element={<DesignAssumptions />} />
              <Route path="/resetpassword" element={<ResetPassword />} />
              <Route path="/projectNotFound" element={<NotFound />} />
              <Route path="/aurora/design/config/:projectId/:designId/" element={<ProtectedRoute isAuthenticated={true} accessibleUrl={isAuroraAccessible}><AuroraDesignConfigWrapper /></ProtectedRoute>} />

              <Route path="/combinedprojects/:combinedProjectId" element={<CombinedProjectsWrapper />} />
              <Route path="/forgotpassword/*" element={<PublicRoute isAuthenticated={user.isAuthenticated}><ForgotPasswordWrapper /></PublicRoute>} />
              <Route path="/project/screenshot/:productId/:projectId/:arrayId" element={<ScreenshotContainerWrapper />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
        </div>
    </AppErrorBoundary>
  );
};

/* ---------------- REDUX ---------------- */

// Legacy exports deprecated - use navigationService instead
// export const { push, replace, go, goBack, goForward } = AppHistory;

const mapStateToProps = (state: any) => ({
  app: state.app,
  user: state.user,
  projectConfiguration: state.projectConfiguration,
  location: state.router?.location?.pathname || '/',
});

const connector = connect(mapStateToProps);
const ConnectedApp = connector(App);

// connect() bails out when its mapped state is unchanged, which also skips
// the <Routes> update. After the first save, a later continue-to-BOM often
// changes only the URL (same project id), so the address bar moves to the
// BOM route while the design page stays on "Opening project..." until a
// hard refresh. Passing the router location in as a prop forces that render.
// Named routerLocation so it does not clash with mapState's `location` string.
const AppWithRouterLocation: React.FC = () => {
  const location = useLocation();
  const RoutedApp = ConnectedApp as React.ComponentType<{ routerLocation: typeof location }>;
  return <RoutedApp routerLocation={location} />;
};

export default AppWithRouterLocation;
